/**
 * Shared, mutable client state (game, online, wallet, profile, team, stats,
 * account/session, view tabs, table interaction, media).
 * Reassignable pieces are exported as `let` + a setter so importing modules read
 * the live binding and update it through the setter (ESM forbids reassigning an
 * imported binding directly). This module imports nothing from the app, so any
 * module can depend on it without creating an import cycle.
 */

// -- game --------------------------------------------------------------------
export let S: any = null;
export function setS(v: any): void { S = v; }

// -- online ------------------------------------------------------------------
export const NET: {
  active: boolean; snap: any; peers: Record<string, any>; mySeat: number | null;
  code: string | null; myName: string;
} = { active: false, snap: null, peers: {}, mySeat: null, code: null, myName: '' };
export let socket: any = null;
export function setSocket(v: any): void { socket = v; }

// -- wallet ------------------------------------------------------------------
export interface Wallet {
  coins: number; owned: string[]; active: string[]; collect: any[];
  mint?: any; pro?: any; vouchers?: any;
}
export let WALLET: Wallet = { coins: 500, owned: [], active: [], collect: [] };
export function setWallet(v: any): void { WALLET = v; }
try {
  const w = JSON.parse(localStorage.getItem('wallet') as string);
  if (w && typeof w.coins === 'number') WALLET = w;
} catch { /* no storage */ }
if (!Array.isArray(WALLET.collect)) WALLET.collect = [];
/* la tienda / PRO CLUB fields for wallets saved before they existed */
if (!WALLET.mint || typeof WALLET.mint !== 'object') WALLET.mint = {};
if (!('pro' in WALLET)) WALLET.pro = null;
if (!WALLET.vouchers) WALLET.vouchers = { tables: 0, fichas: 0, t50: 0, t25: 0, cheer: false };

// -- profile -----------------------------------------------------------------
export let PROFILE: any = { name: '', avatar: '\u{1F525}', country: '\u{1F1E9}\u{1F1F4}' };
export function setProfile(v: any): void { PROFILE = v; }
try { const p = JSON.parse(localStorage.getItem('profile') as string); if (p) PROFILE = p; } catch { /* */ }

// -- team --------------------------------------------------------------------
export let TEAM: any = { name: '', flag: '\u{1F1E9}\u{1F1F4}', motto: '' };
export function setTeam(v: any): void { TEAM = v; }
try { const tm = JSON.parse(localStorage.getItem('team') as string); if (tm) TEAM = tm; } catch { /* */ }
if (!Array.isArray(TEAM.members)) TEAM.members = [];

// -- stats -------------------------------------------------------------------
export let STATS: any = { hands: 0, handsWon: 0, capicus: 0, chuchazos: 0, tranquesWon: 0, matches: 0, matchesWon: 0 };
export function setStats(v: any): void { STATS = v; }
try { const s2 = JSON.parse(localStorage.getItem('stats') as string); if (s2) STATS = s2; } catch { /* */ }

// -- settings / assists ------------------------------------------------------
// `hints` toggles the on-table drop-spot zones + playable/dead highlighting.
// `level` remembers the skill preset chosen at first game (null = ask once).
export const SETTINGS: { hints: boolean; level: string | null } = { hints: true, level: null };
try { Object.assign(SETTINGS, JSON.parse(localStorage.getItem('mdsettings') as string) || {}); } catch { /* */ }
export function saveSettings(): void { try { localStorage.setItem('mdsettings', JSON.stringify(SETTINGS)); } catch { /* */ } }

// -- account / session -------------------------------------------------------
export let ACCOUNT: any = { fullname: '', email: '', phone: '', joined: 0 };
export function setACCOUNT(v: any): void { ACCOUNT = v; }
try { ACCOUNT = Object.assign(ACCOUNT, JSON.parse(localStorage.getItem('account') || '{}')); } catch { /* */ }

/** Bearer token for the cloud API (empty = guest / offline build). */
export let API_TOKEN = '';
export function setAPI_TOKEN(v: string): void { API_TOKEN = v; }
try { API_TOKEN = localStorage.getItem('mdtoken') || ''; } catch { /* */ }

/** Debounce timer handle for cloudSync(). */
export let cloudT: any = null;
export function setCloudT(v: any): void { cloudT = v; }

// -- admin DB (founder's local ledger; seeded/migrated by admin/portal initAdminDB)
export let ADMIN_DB: any = null;
export function setADMIN_DB(v: any): void { ADMIN_DB = v; }
try { ADMIN_DB = JSON.parse(localStorage.getItem('admindb') as string); } catch { /* */ }

// -- match history -----------------------------------------------------------
export let MATCH_LOG: any[] = [];
export function setMATCH_LOG(v: any[]): void { MATCH_LOG = v; }
try { const m = JSON.parse(localStorage.getItem('matchlog') as string); if (Array.isArray(m)) MATCH_LOG = m; } catch { /* */ }

// -- view state (store page, dashboard) --------------------------------------
export let tpTab = 'mesas';
export function setTpTab(v: string): void { tpTab = v; }
export let dashTab = 'mesas';
export function setDashTab(v: string): void { dashTab = v; }
export let dashView = 'perfil';
export function setDashView(v: string): void { dashView = v; }
export let adminUserSel: string | null = null;
export function setAdminUserSel(v: string | null): void { adminUserSel = v; }

// -- table interaction (offline game) ----------------------------------------
export let firstHandOfMatch = true;
export function setFirstHandOfMatch(v: boolean): void { firstHandOfMatch = v; }
export let pendingTile: any = null;   // tile awaiting side choice
export function setPendingTile(v: any): void { pendingTile = v; }
/* previous board ends, for capicú detection */
export let prevLeftEnd: any = null, prevRightEnd: any = null;
export function setPrevLeftEnd(v: any): void { prevLeftEnd = v; }
export function setPrevRightEnd(v: any): void { prevRightEnd = v; }
/* drag & drop: the ficha being dragged and its index in the rack */
export let dragTile: any = null, dragIdx: any = null;
export function setDragTile(v: any): void { dragTile = v; }
export function setDragIdx(v: any): void { dragIdx = v; }

// -- media (your camera / mic stream) ----------------------------------------
export let myStream: MediaStream | null = null;
export function setMyStream(v: MediaStream | null): void { myStream = v; }
